/**
 * Onboarding Modal
 * Main modal wrapper that orchestrates the onboarding flow
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from './hooks/useOnboarding';
import { SplashScreen } from './screens/SplashScreen';
import { HomePreview } from './screens/HomePreview';
import { AskSwirlPreview } from './screens/AskSwirlPreview';
import { FocusPreview } from './screens/FocusPreview';
import { CreateFirstFocus } from './screens/CreateFirstFocus';
import type { OnboardingStep } from './types';

export const OnboardingModal: React.FC = () => {
  const { user } = useAuth();
  const { isOpen, closeOnboarding, markAsComplete } = useOnboarding();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  const [isClosing, setIsClosing] = useState(false);

  // Reset to step 1 when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsClosing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const totalSteps = 5;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => (prev + 1) as OnboardingStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as OnboardingStep);
    }
  };

  const handleClose = async () => {
    if (user) {
      await markAsComplete(user.id);
    }
    setIsClosing(true);
    setTimeout(() => {
      closeOnboarding();
    }, 300);
  };

  const handleComplete = async () => {
    if (user) {
      await markAsComplete(user.id);
    }
    setIsClosing(true);
    setTimeout(() => {
      closeOnboarding();
    }, 300);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isClosing ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        // Close if clicking the backdrop
        if (e.target === e.currentTarget && currentStep !== 1) {
          handleClose();
        }
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: isClosing ? 0.95 : 1, opacity: isClosing ? 0 : 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full h-full max-w-5xl max-h-[90vh] m-4 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Only show after splash screen */}
        {currentStep > 1 && (
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-6 bg-gradient-to-b from-white/90 to-transparent backdrop-blur-sm">
            {/* Back Button */}
            {currentStep > 2 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Back</span>
              </button>
            )}
            {currentStep === 2 && <div />}

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="ml-auto p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              aria-label="Close onboarding"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Screen Content */}
        <div className="flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="splash"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-full"
              >
                <SplashScreen onComplete={handleNext} />
              </motion.div>
            )}
            
            {currentStep === 2 && (
              <motion.div
                key="home"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="h-full"
              >
                <HomePreview onNext={handleNext} />
              </motion.div>
            )}
            
            {currentStep === 3 && (
              <motion.div
                key="ask"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="h-full"
              >
                <AskSwirlPreview onNext={handleNext} />
              </motion.div>
            )}
            
            {currentStep === 4 && (
              <motion.div
                key="focus"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="h-full"
              >
                <FocusPreview onNext={handleNext} />
              </motion.div>
            )}
            
            {currentStep === 5 && (
              <motion.div
                key="create"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="h-full"
              >
                <CreateFirstFocus onComplete={handleComplete} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Progress Dots - Only show after splash screen */}
        {currentStep > 1 && (
          <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2 pointer-events-none">
            {Array.from({ length: totalSteps - 1 }, (_, i) => i + 2).map((step) => (
              <motion.div
                key={step}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1 * (step - 1) }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  step === currentStep
                    ? 'bg-purple-500 w-8'
                    : step < currentStep
                    ? 'bg-purple-300'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};





